import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  IconButton,
  Tooltip,
  InputAdornment,
  Menu,
  MenuList,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextareaAutosize,
} from '@mui/material';
import {
  Search,
  Visibility,
  Edit,
  Warning,
  CheckCircle,
  Schedule,
  Error,
  Info,
  MoreVert,
  Download,
  Assignment,
  Refresh,
  Update,
  Add as AddIcon,
} from '@mui/icons-material';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom'; // Add useNavigate
import appConfig from '../config/app';
import RMAFormModal from './RMAFormModal'; // Import the modal component

// API service functions
const createApiService = (auth) => ({
  async getRMARequests() {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;

    const response = await fetch(`${appConfig.api.baseUrl}/rma/review/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  },

  async startReview(rmaId) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;

    const response = await fetch(`${appConfig.api.baseUrl}/rma/${rmaId}/start-review/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  },

  async updateStatus(rmaId, statusData) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;

    const response = await fetch(`${appConfig.api.baseUrl}/rma/${rmaId}/update-status/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
      body: JSON.stringify(statusData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  },

  async downloadAttachment(attachmentId) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;

    const response = await fetch(`${appConfig.api.baseUrl}/rma/attachments/${attachmentId}/download/`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.blob();
  },
});

const RMAList = ({ onSelectRMA, onRefresh, refreshTrigger, userType }) => {
  const auth = useAuth();
  const navigate = useNavigate(); // Add navigate hook
  const [rmas, setRmas] = useState([]);
  const [filteredRmas, setFilteredRmas] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [sortBy, setSortBy] = useState('submitted_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRMA, setSelectedRMA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [changeReason, setChangeReason] = useState('');
  const [newRMAModalOpen, setNewRMAModalOpen] = useState(false); // State for new RMA modal

  const apiService = createApiService(auth);

  // Status options from backend
  const statusOptions = [
    'draft', 'submitted', 'under_review', 'needs_info',
    'approved', 'rejected', 'in_progress', 'completed', 'cancelled',
  ];

  // Load RMA data from API
  const loadRMAs = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getRMARequests();
      setRmas(data.rmas || []);
    } catch (err) {
      setError(err.message);
      console.error('Error loading RMAs:', err);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadRMAs();
  }, []);

  // Refresh when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger > 0) {
      loadRMAs();
    }
  }, [refreshTrigger]);

  // Filter and sort RMAs
  useEffect(() => {
    let filtered = rmas.filter(rma => {
      const matchesSearch =
        rma.rma_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rma.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rma.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rma.contact_person?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === 'all' || rma.status === statusFilter;
      const matchesPriority = priorityFilter === 'all' || rma.priority === priorityFilter;

      return matchesSearch && matchesStatus && matchesPriority;
    });

    // Sort
    filtered.sort((a, b) => {
      const aValue = a[sortBy];
      const bValue = b[sortBy];

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredRmas(filtered);
    setPage(0); // Reset to first page when filtering
  }, [rmas, searchTerm, statusFilter, priorityFilter, sortBy, sortOrder]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleSelectRMA = (rmaId, action = 'view') => {
    if (onSelectRMA) {
      onSelectRMA(rmaId, action);
    }
  };

  const handleStartReview = async (rmaId) => {
    try {
      await apiService.startReview(rmaId);
      setSnackbarMessage('Review started successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      loadRMAs(); // Refresh the list
      setAnchorEl(null);
    } catch (err) {
      setSnackbarMessage('Error starting review: ' + err.message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus) {
      setSnackbarMessage('Please select a new status');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    try {
      await apiService.updateStatus(selectedRMA?.id, {
        status: newStatus,
        change_reason: changeReason,
      });
      setSnackbarMessage(`Status updated to ${newStatus.replace('_', ' ').toUpperCase()}`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setStatusDialogOpen(false);
      setNewStatus('');
      setChangeReason('');
      loadRMAs(); // Refresh the list
      setAnchorEl(null);
    } catch (err) {
      setSnackbarMessage('Error updating status: ' + err.message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRMA(null);
  };

  const handleStatusDialogOpen = (rma) => {
    setSelectedRMA(rma);
    setStatusDialogOpen(true);
    setNewStatus('');
    setChangeReason('');
  };

  const handleRmaCreated = () => {
    loadRMAs(); // Refresh the RMA list
  };

  const handleNewRMAModalOpen = () => {
    setNewRMAModalOpen(true);
    setAnchorEl(null); // Close the menu
  };

  const handleStatusDialogClose = () => {
    setStatusDialogOpen(false);
    setNewStatus('');
    setChangeReason('');
  };

  const showNotification = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted': return 'primary';
      case 'under_review': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'needs_info': return 'info';
      case 'return_to_producer': return 'warning';
      case 'return_to_customer': return 'success';
      case 'in_progress': return 'info';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'submitted': return <Info />;
      case 'under_review': return <Schedule />;
      case 'approved': return <CheckCircle />;
      case 'rejected': return <Error />;
      case 'needs_info': return <Warning />;
      default: return <Info />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={loadRMAs}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          RMA Requests
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {userType === 'serviceman' && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleNewRMAModalOpen}
            >
              New RMA
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadRMAs}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search RMA number, company, product..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  {statusOptions.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status.replace('_', ' ').toUpperCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography variant="body2" color="text.secondary">
                {filteredRmas.length} of {rmas.length} RMAs
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'rma_number'}
                    direction={sortBy === 'rma_number' ? sortOrder : 'asc'}
                    onClick={() => handleSort('rma_number')}
                  >
                    RMA Number
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'company_name'}
                    direction={sortBy === 'company_name' ? sortOrder : 'asc'}
                    onClick={() => handleSort('company_name')}
                  >
                    Company
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'product_name'}
                    direction={sortBy === 'product_name' ? sortOrder : 'asc'}
                    onClick={() => handleSort('product_name')}
                  >
                    Product
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'status'}
                    direction={sortBy === 'status' ? sortOrder : 'asc'}
                    onClick={() => handleSort('status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'submitted_at'}
                    direction={sortBy === 'submitted_at' ? sortOrder : 'asc'}
                    onClick={() => handleSort('submitted_at')}
                  >
                    Submitted
                  </TableSortLabel>
                </TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRmas
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((rma) => (
                  <TableRow
                    key={rma.id}
                    hover
                    onClick={() => handleSelectRMA(rma.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {rma.rma_number}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {rma.company_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {rma.product_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(rma.status)}
                        label={rma.status.replace('_', ' ').toUpperCase()}
                        color={getStatusColor(rma.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(rma.submitted_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectRMA(rma.id);
                            }}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="More Actions">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedRMA(rma);
                              setAnchorEl(e.currentTarget);
                            }}
                          >
                            <MoreVert />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredRmas.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuList>
          <MenuItem onClick={() => handleSelectRMA(selectedRMA?.id)}>
            <ListItemIcon>
              <Visibility />
            </ListItemIcon>
            <ListItemText>View Details</ListItemText>
          </MenuItem>
          {selectedRMA?.status === 'submitted' && (
            <MenuItem onClick={() => handleStartReview(selectedRMA?.id)}>
              <ListItemIcon>
                <Assignment />
              </ListItemIcon>
              <ListItemText>Start Review</ListItemText>
            </MenuItem>
          )}
          <MenuItem onClick={() => handleStatusDialogOpen(selectedRMA)}>
            <ListItemIcon>
              <Update />
            </ListItemIcon>
            <ListItemText>Update Status</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleSelectRMA(selectedRMA?.id, 'edit')}>
            <ListItemIcon>
              <Edit />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        </MenuList>
      </Menu>

      {/* Status Update Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={handleStatusDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update RMA Status</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>New Status</InputLabel>
              <Select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                label="New Status"
              >
                {statusOptions.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status.replace('_', ' ').toUpperCase()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextareaAutosize
              minRows={4}
              placeholder="Reason for status change (optional)"
              value={changeReason}
              onChange={(e) => setChangeReason(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '8px',
                border: '1px solid #ccc',
                fontFamily: 'inherit',
                fontSize: '1rem',
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleStatusDialogClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateStatus}
            disabled={!newStatus}
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* New RMA Modal */}
      <RMAFormModal
        open={newRMAModalOpen}
        onClose={() => setNewRMAModalOpen(false)}
        onMessage={showNotification}
        onRmaCreated={handleRmaCreated}
        navigate={navigate}
        userType={userType}
        auth={auth}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RMAList;